/**
 * Application root.
 *
 * Two responsibilities, in this order:
 *
 * 1. **Start the silent session restore** (`useSessionBootstrap`), which is what turns a valid refresh cookie back
 *    into an in-memory access token and flips `isBootstrapped` so the guards can decide;
 * 2. render the route tree behind one `Suspense`, a fallback for the lazily loaded pages that render outside the
 *    shell (the landing page and the public auth screens).
 */

import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/ui/surface';
import { useSessionBootstrap } from '@/features/auth/hooks/useAuth';
import { AppRoutes } from '@/routes/AppRoutes';

/** Full-screen fallback used while a lazily loaded page is being fetched. */
function RouteFallback() {
  const { t } = useTranslation();

  return (
    <div className="grid min-h-screen place-items-center bg-background" aria-busy="true">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <Spinner className="h-6 w-6" />
        <p className="text-sm">{t('common.loading')}</p>
      </div>
    </div>
  );
}

/** Root component of the SPA. */
export function App() {
  useSessionBootstrap();

  return (
    <Suspense fallback={<RouteFallback />}>
      <AppRoutes />
    </Suspense>
  );
}
